import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import { toast } from '../components/Toast';

const STATUS_TABS = [
  { key: 'pending_review', label: 'Pending Review', color: '#D97706', bg: '#FEF3C7' },
  { key: 'sent',           label: 'Released',       color: '#059669', bg: '#D1FAE5' },
  { key: 'rejected',       label: 'Rejected',       color: '#DC2626', bg: '#FEE2E2' },
];

const ReportQueue = ({ user }) => {
  const [activeTab, setActiveTab]       = useState('pending_review');
  const [reports, setReports]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal]       = useState(false);
  const [saving, setSaving]             = useState(false);
  const [releasing, setReleasing]       = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [preview, setPreview]             = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview]     = useState(false);
  const [editForm, setEditForm]         = useState({
    report_edited_start: '',
    report_edited_end: '',
    report_edited_notes: '',
  });

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/report-queue?status=' + activeTab);
      setReports(res.data.data || []);
    } catch (err) {
      toast.error('Error loading report queue');
    }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const openReview = (report) => {
    setSelectedReport(report);
    setEditForm({
      report_edited_start: report.report_edited_start
        ? toLocalDatetimeInput(report.report_edited_start)
        : (report.service_start_time ? toLocalDatetimeInput(report.service_start_time) : ''),
      report_edited_end: report.report_edited_end
        ? toLocalDatetimeInput(report.report_edited_end)
        : (report.service_end_time ? toLocalDatetimeInput(report.service_end_time) : ''),
      report_edited_notes: report.report_edited_notes || report.technician_notes || '',
    });
    setShowModal(true);
  };

  const toLocalDatetimeInput = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const pad = n => String(n).padStart(2, '0');
    return d.getFullYear() + '-' + pad(d.getMonth()+1) + '-' + pad(d.getDate()) +
           'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
  };

  const formatDisplay = (ts) => {
    if (!ts) return '--';
    return new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric',
      year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getTimeInQueue = (completedAt) => {
    if (!completedAt) return '--';
    const mins = Math.floor((Date.now() - new Date(completedAt)) / 60000);
    if (mins < 60) return mins + 'm';
    if (mins < 1440) return Math.floor(mins/60) + 'h ' + (mins%60) + 'm';
    return Math.floor(mins/1440) + 'd ' + Math.floor((mins%1440)/60) + 'h';
  };

  const handleSaveEdits = async () => {
    setSaving(true);
    try {
      await api.put('/report-queue/' + selectedReport.id + '/edit', editForm);
      toast.success('Edits saved');
      fetchReports();
    } catch (err) {
      toast.error('Error saving edits');
    }
    setSaving(false);
  };

  const handleRelease = () => {
    setConfirmDialog({
      message: 'Release this report to the client? This will generate the PDF and send it via email.',
      confirmText: 'Release Report',
      confirmColor: '#059669',
      icon: '📤',
      onConfirm: async () => {
        setConfirmDialog(null);
        setReleasing(true);
        try {
          // Save edits first
          await api.put('/report-queue/' + selectedReport.id + '/edit', editForm);
          await api.post('/report-queue/' + selectedReport.id + '/release');
          toast.success('Report released and sent to client');
          setShowModal(false);
          fetchReports();
        } catch (err) {
          toast.error('Error releasing report: ' + (err.response?.data?.message || err.message));
        }
        setReleasing(false);
      }
    });
  };

  const handleReject = () => {
    setConfirmDialog({
      message: 'Reject this report? It will be moved to the Rejected queue.',
      confirmText: 'Reject',
      confirmColor: '#DC2626',
      icon: '⚠️',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.post('/report-queue/' + selectedReport.id + '/reject');
          toast.success('Report rejected');
          setShowModal(false);
          fetchReports();
        } catch (err) {
          toast.error('Error rejecting report');
        }
      }
    });
  };

  const loadPreview = async () => {
    if (!selectedReport) return;
    setPreviewLoading(true);
    setShowPreview(true);
    try {
      const res = await api.get('/report-queue/' + selectedReport.id + '/preview');
      setPreview(res.data.report);
    } catch (err) {
      toast.error('Could not load preview');
      setShowPreview(false);
    }
    setPreviewLoading(false);
  };

  const fmtDT = (d) => d ? new Date(d).toLocaleString('en-US', { month:'short', day:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—';
  const fmtDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month:'long', day:'numeric', year:'numeric' }) : '—';

  return (
    <Layout user={user}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#111827' }}>Report Queue</h1>
            <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#6B7280' }}>
              Review, edit and release service reports before they reach the client
            </p>
          </div>
          <button onClick={fetchReports} style={{ padding: '8px 16px', background: '#F3F4F6',
            border: '1px solid #E5E7EB', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
            🔄 Refresh
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid #E5E7EB', paddingBottom: '0' }}>
          {STATUS_TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: '14px',
                fontWeight: activeTab === tab.key ? '700' : '500',
                color: activeTab === tab.key ? tab.color : '#6B7280',
                background: 'transparent',
                borderBottom: activeTab === tab.key ? '2px solid ' + tab.color : '2px solid transparent',
                marginBottom: '-2px' }}>
              {tab.label}
              {tab.key === 'pending_review' && reports.length > 0 && activeTab === 'pending_review' && (
                <span style={{ marginLeft: '8px', background: tab.color, color: 'white',
                  borderRadius: '10px', fontSize: '11px', padding: '2px 7px', fontWeight: '700' }}>
                  {reports.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#9CA3AF' }}>Loading...</div>
        ) : reports.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#9CA3AF', background: 'white',
            borderRadius: '10px', border: '1px solid #E8E8E8' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>
              {activeTab === 'pending_review' ? '✅' : activeTab === 'sent' ? '📬' : '🗂️'}
            </div>
            <p style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
              {activeTab === 'pending_review' ? 'No reports pending review' : activeTab === 'sent' ? 'No released reports' : 'No rejected reports'}
            </p>
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: '10px', border: '1px solid #E8E8E8', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                  {['WO #', 'Client', 'Facility', 'Technician', 'Completed', 'Time in Queue', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px',
                      fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reports.map((r, i) => {
                  const tab = STATUS_TABS.find(t => t.key === r.report_status) || STATUS_TABS[0];
                  const inQueue = getTimeInQueue(r.completed_at);
                  const isLate = r.report_status === 'pending_review' &&
                    (Date.now() - new Date(r.completed_at)) > 24*60*60*1000;
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #F3F4F6',
                      background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                      <td style={{ padding: '14px 16px', fontWeight: '700', color: '#1A3A2A', fontSize: '14px' }}>
                        {r.order_number || '#' + r.id}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', color: '#374151' }}>{r.client_name}</td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', color: '#374151' }}>{r.facility_name}</td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', color: '#374151' }}>{r.technician_name || '--'}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#6B7280' }}>{formatDisplay(r.completed_at)}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px',
                        color: isLate ? '#DC2626' : '#6B7280', fontWeight: isLate ? '700' : '400' }}>
                        {isLate ? '⚠️ ' : ''}{inQueue}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: tab.bg, color: tab.color, padding: '3px 10px',
                          borderRadius: '10px', fontSize: '12px', fontWeight: '600' }}>
                          {tab.label}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {r.report_status === 'pending_review' && (
                          <button onClick={() => openReview(r)}
                            style={{ padding: '7px 16px', background: '#2D5C3F', color: 'white',
                              border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                            Review
                          </button>
                        )}
                        {r.report_status !== 'pending_review' && (
                          <button onClick={() => openReview(r)}
                            style={{ padding: '7px 16px', background: 'white', color: '#6B7280',
                              border: '1px solid #E5E7EB', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                            View
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showModal && selectedReport && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => { setShowModal(false); setPreview(null); setShowPreview(false); }}>
          <div style={{ background: 'white', borderRadius: '12px', width: '100%', maxWidth: '680px',
            maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>

            {/* Modal Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #E5E7EB',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: '#1A3A2A', borderRadius: '12px 12px 0 0' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'white' }}>
                  Review Report — {selectedReport.order_number || '#' + selectedReport.id}
                </h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#D4AF37' }}>
                  {selectedReport.client_name} — {selectedReport.facility_name}
                </p>
              </div>
              <button onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ padding: '24px' }}>
              {/* Info row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                {[
                  { label: 'Technician', value: selectedReport.technician_name || '--' },
                  { label: 'Completed', value: formatDisplay(selectedReport.completed_at) },
                  { label: 'Time in Queue', value: getTimeInQueue(selectedReport.completed_at) },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: '#F9FAFB', borderRadius: '8px', padding: '12px' }}>
                    <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>{label}</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Editable fields */}
              {selectedReport.report_status === 'pending_review' && (
                <div style={{ border: '2px solid #2D5C3F33', borderRadius: '10px', padding: '16px', marginBottom: '20px', background: '#F0F7F4' }}>
                  <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: '700', color: '#1A3A2A' }}>
                    ✏️ Edit Before Sending
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>
                        Service Start Time
                      </label>
                      <input type="datetime-local" value={editForm.report_edited_start}
                        onChange={e => setEditForm({ ...editForm, report_edited_start: e.target.value })}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #DDD',
                          borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>
                        Service End Time
                      </label>
                      <input type="datetime-local" value={editForm.report_edited_end}
                        onChange={e => setEditForm({ ...editForm, report_edited_end: e.target.value })}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #DDD',
                          borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>
                      Technician Notes (editable before sending)
                    </label>
                    <textarea value={editForm.report_edited_notes}
                      onChange={e => setEditForm({ ...editForm, report_edited_notes: e.target.value })}
                      rows={4} style={{ width: '100%', padding: '8px 10px', border: '1px solid #DDD',
                        borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', resize: 'vertical' }} />
                  </div>
                  <p style={{ fontSize: '12px', color: '#6B7280', margin: '8px 0 0' }}>
                    ℹ️ Original notes: "{selectedReport.technician_notes || 'No notes'}"
                  </p>
                </div>
              )}

              {/* View only — released/rejected */}
              {selectedReport.report_status !== 'pending_review' && selectedReport.report_edited_notes && (
                <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#9CA3AF', marginBottom: '6px' }}>EDITED NOTES</div>
                  <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>{selectedReport.report_edited_notes}</p>
                </div>
              )}

              {/* Actions */}
              {selectedReport.report_status === 'pending_review' && (
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '8px',
                  borderTop: '1px solid #F3F4F6' }}>
                  <button onClick={handleReject}
                    style={{ padding: '10px 20px', background: 'white', color: '#DC2626',
                      border: '1px solid #DC2626', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                    Reject
                  </button>
                  <button onClick={() => { setShowPreview(p => !p); if (!preview) loadPreview(); }}
                    style={{ padding: '10px 20px', background: showPreview ? '#EEF7F0' : 'white', color: '#2D5C3F',
                      border: '1px solid #2D5C3F', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                    {previewLoading ? '⏳ Loading...' : showPreview ? '🔼 Hide Preview' : '👁 Preview Report'}
                  </button>
                  <button onClick={handleSaveEdits} disabled={saving}
                    style={{ padding: '10px 20px', background: '#F3F4F6', color: '#374151',
                      border: '1px solid #E5E7EB', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                    {saving ? 'Saving...' : '💾 Save Edits'}
                  </button>
                  <button onClick={handleRelease} disabled={releasing}
                    style={{ padding: '10px 24px', background: releasing ? '#9CA3AF' : '#2D5C3F', color: 'white',
                      border: 'none', borderRadius: '6px', cursor: releasing ? 'not-allowed' : 'pointer',
                      fontWeight: '600', fontSize: '14px' }}>
                    {releasing ? 'Releasing...' : '📤 Release to Client'}
                  </button>
                </div>
              )}

              {/* ── PREVIEW PANEL ── */}
              {showPreview && (
                <div style={{ marginTop: '20px', border: '2px solid #2D5C3F33', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ background: '#1A3A2A', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#D4AF37', fontWeight: '700', fontSize: '14px' }}>👁 Report Preview</span>
                    <span style={{ color: '#9CA3AF', fontSize: '12px' }}>Read-only — edits above will be reflected after saving</span>
                  </div>
                  {previewLoading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>Generating preview...</div>
                  ) : preview && (
                    <div style={{ padding: '20px', maxHeight: '500px', overflowY: 'auto', background: '#FAFAFA' }}>

                      {/* Header info */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                        {[
                          { label: 'Service Type', value: preview.header.service_type },
                          { label: 'Service Date', value: preview.header.service_date },
                          { label: 'Start Time', value: fmtDT(preview.header.service_start_time) },
                          { label: 'End Time', value: fmtDT(preview.header.service_end_time) },
                          { label: 'Technician', value: preview.header.technician },
                          { label: 'License', value: preview.header.license ? preview.header.license.number + ' (' + preview.header.license.state + ')' : '—' },
                        ].map(({ label, value }) => (
                          <div key={label} style={{ background: 'white', borderRadius: '6px', padding: '10px 12px', border: '1px solid #E5E7EB' }}>
                            <div style={{ fontSize: '10px', color: '#9CA3AF', fontWeight: '600', textTransform: 'uppercase', marginBottom: '2px' }}>{label}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{value || '—'}</div>
                          </div>
                        ))}
                      </div>

                      {/* Executive Summary */}
                      {preview.executive_summary && (
                        <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#2D5C3F', marginBottom: '6px', textTransform: 'uppercase' }}>📋 Executive Summary</div>
                          <p style={{ margin: 0, fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>{preview.executive_summary}</p>
                        </div>
                      )}

                      {/* Technician Notes */}
                      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#2D5C3F', marginBottom: '6px', textTransform: 'uppercase' }}>📝 Technician Notes</div>
                        <p style={{ margin: 0, fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>
                          {editForm.report_edited_notes || preview.technician_notes || 'No notes'}
                        </p>
                      </div>

                      {/* Stats */}
                      {preview.stats && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '14px' }}>
                          {[
                            { label: 'Devices', value: preview.stats.total_devices },
                            { label: 'Areas', value: preview.stats.total_areas },
                            { label: 'Activity', value: preview.stats.devices_with_activity + ' devices' },
                            { label: 'Activity Rate', value: preview.stats.activity_rate + '%' },
                          ].map(({ label, value }) => (
                            <div key={label} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
                              <div style={{ fontSize: '18px', fontWeight: '700', color: '#2D5C3F' }}>{value}</div>
                              <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{label}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Products Applied */}
                      {preview.products_applied && preview.products_applied.length > 0 && (
                        <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#2D5C3F', marginBottom: '10px', textTransform: 'uppercase' }}>🧪 Products Applied</div>
                          {preview.products_applied.map((p, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0',
                              borderBottom: i < preview.products_applied.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                              <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{p.product_name || p.name}</span>
                              <span style={{ fontSize: '12px', color: '#6B7280' }}>{p.quantity} {p.unit} {p.method ? '— ' + p.method : ''}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Conducive Conditions */}
                      {preview.conducive_conditions && preview.conducive_conditions.length > 0 && (
                        <div style={{ background: 'white', border: '1px solid #FEF3C7', borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#D97706', marginBottom: '10px', textTransform: 'uppercase' }}>⚠️ Conducive Conditions ({preview.conducive_conditions.length})</div>
                          {preview.conducive_conditions.slice(0, 5).map((c, i) => (
                            <div key={i} style={{ padding: '6px 0', borderBottom: i < Math.min(preview.conducive_conditions.length, 5) - 1 ? '1px solid #F3F4F6' : 'none' }}>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{c.condition} — <span style={{ color: '#6B7280', fontWeight: '400' }}>{c.location}</span></div>
                              {c.description && <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{c.description}</div>}
                            </div>
                          ))}
                          {preview.conducive_conditions.length > 5 && (
                            <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '8px' }}>+{preview.conducive_conditions.length - 5} more conditions in full report</div>
                          )}
                        </div>
                      )}

                      {/* Overall Risk */}
                      {preview.overall_risk && (
                        <div style={{ background: preview.overall_risk === 'high' ? '#FEE2E2' : preview.overall_risk === 'medium' ? '#FEF3C7' : '#D1FAE5',
                          border: '1px solid', borderColor: preview.overall_risk === 'high' ? '#FCA5A5' : preview.overall_risk === 'medium' ? '#FDE68A' : '#6EE7B7',
                          borderRadius: '8px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '20px' }}>{preview.overall_risk === 'high' ? '🔴' : preview.overall_risk === 'medium' ? '🟡' : '🟢'}</span>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Overall Risk Level</div>
                            <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827', textTransform: 'capitalize' }}>{preview.overall_risk}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          confirmColor={confirmDialog.confirmColor}
          icon={confirmDialog.icon}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </Layout>
  );
};

export default ReportQueue;
